import { RESOURCE_KINDS } from "@empire/shared";
import { ResourceIcon } from "./ResourceIcon.js";

type Loot = { wood: number; clay: number; iron: number; grain: number } | undefined;

/**
 * What a battle moved between the two villages (D-118), from one side's view.
 *
 * Nothing at all is drawn for a battle that took nothing for an ordinary
 * reason (the army was held); the two unusual reasons say so, because a
 * breach that brings home nothing otherwise looks like a bug.
 */
export function LootLine({
  loot,
  note,
  side,
  testId,
  economics,
}: {
  loot: Loot;
  note?: "farmed" | "empty" | "protected" | undefined;
  economics?: { deposited?: Loot; overflow?: Loot; replacementCost?: Loot };
  side: "attacker" | "defender";
  testId?: string;
}): JSX.Element | null {
  if (side === "attacker" && economics?.replacementCost) {
    const deposited = economics.deposited;
    const lost = economics.replacementCost;
    const overflow = economics.overflow;
    return (
      <span className="block text-[11px] text-ink-dim" data-testid={testId}>
        <span className="block">Raid balance: deposited loot minus troop replacement</span>
        {RESOURCE_KINDS.map((k) => (
          <span key={k} className="mr-3 inline-flex items-center gap-1">
            <ResourceIcon kind={k} size={11} />
            {deposited?.[k] ?? 0} − {lost[k]} = {(deposited?.[k] ?? 0) - lost[k]}
          </span>
        ))}
        {overflow && RESOURCE_KINDS.some((k) => overflow[k] > 0) ? (
          <span className="block text-warn">
            Storage overflow lost: {RESOURCE_KINDS.map((k) => `${k} ${overflow[k]}`).join(" · ")}
          </span>
        ) : null}
        {note ? (
          <span className="block">
            {note === "protected"
              ? "Victim protection limited this raid."
              : note === "farmed"
                ? "Daily loot limit reached."
                : "No unprotected stock available."}
          </span>
        ) : null}
      </span>
    );
  }
  if (!loot) {
    if (note === "protected")
      return (
        <span className="block text-[11px] text-ink-dim" data-testid={testId}>
          Protected: the village's loss limit for this eight-hour window was reached.
        </span>
      );
    if (note === "farmed") {
      return (
        <span className="block text-[11px] text-warn" data-testid={testId}>
          {side === "attacker"
            ? "Nothing taken: you already raided this village 3 times today."
            : "Nothing taken: they had raided you 3 times today."}
        </span>
      );
    }
    if (note === "empty") {
      return (
        <span className="block text-[11px] text-ink-dim" data-testid={testId}>
          Nothing to take: everything left was protected.
        </span>
      );
    }
    return null;
  }
  const total = RESOURCE_KINDS.reduce((n, k) => n + loot[k], 0);
  return (
    <span
      className={`flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] tabular-nums ${side === "attacker" ? "text-ok" : "text-danger"}`}
      data-testid={testId}
    >
      <span className="font-semibold">
        {side === "attacker" ? `Took ${total}` : `Lost ${total}`}
      </span>
      {RESOURCE_KINDS.filter((k) => loot[k] > 0).map((k) => (
        <span key={k} className="flex items-center gap-1">
          <ResourceIcon kind={k} size={11} />
          {loot[k]}
        </span>
      ))}
    </span>
  );
}

/** "18h", "2d 4h" — how long beginner's protection has left, or null if none. */
export function protectionLeft(
  until: string | null | undefined,
  nowMs = Date.now()
): string | null {
  if (!until) return null;
  const ms = new Date(until).getTime() - nowMs;
  if (!(ms > 0)) return null;
  const hours = Math.ceil(ms / 3_600_000);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest > 0 ? `${days}d ${rest}h` : `${days}d`;
}
